/**
 * Copyright © 2025 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

/**
 * Centralized API fetch utility with sliding window session support.
 * 
 * Features:
 * - Automatic JWT token injection in Authorization header
 * - Sliding window session management (1-hour idle timeout)
 * - Automatic token refresh when token is about to expire
 * - Centralized 401 handling with SessionExpiredDialog
 * - Support for JSON and blob responses
 * 
 * Usage:
 *   const data = await apiFetch('/api/endpoint');
 *   const data = await apiFetch('/api/endpoint', { method: 'POST', body: JSON.stringify(payload) });
 *   const blob = await apiFetch('/api/file', { responseType: 'blob' });
 */

import apiHeaders from './apiHeaders';

/**
 * Handle session expiration - clears storage and shows dialog
 */
const handleSessionExpired = (reason) => {
    console.log(`Session expired: ${reason}`);
    sessionStorage.clear();
    
    // Dispatch event for SessionExpiredDialog
    window.dispatchEvent(new CustomEvent('sessionExpired', { 
        detail: { reason: reason || 'token-expired' } 
    }));
};

/**
 * Handle token refresh from sliding window session
 */
const handleTokenRefresh = (response) => {
    const newToken = response.headers.get("X-Token");
    if (newToken) {
        console.log("Token refreshed via sliding window session");
        sessionStorage.setItem("token", newToken);
    }
};

/**
 * Main API fetch function
 * @param {string} endpoint - API endpoint (e.g., '/api/settings')
 * @param {object} options - Fetch options + custom options
 * @param {string} options.responseType - 'json' (default) or 'blob'
 * @returns {Promise<any>} - Response data or null if session expired
 */
const apiFetch = async (endpoint, options = {}) => {
    const token = sessionStorage.getItem("token");
    const baseUrl = process.env.REACT_APP_CONNECTOR_API_URL;
    const { responseType = 'json', ...fetchOptions } = options;

    // Merge headers: apiHeaders + auth token + custom headers
    const headers = {
        ...apiHeaders,
        Authorization: `Bearer ${token}`,
        ...fetchOptions.headers,
    };

    try {
        const response = await fetch(`${baseUrl}${endpoint}`, { 
            ...fetchOptions, 
            headers 
        });

        // Handle 401 - Session expired (idle timeout or token expired)
        if (response.status === 401) {
            const reason = response.headers.get("X-Session-Expired-Reason") || 'token-expired';
            handleSessionExpired(reason);
            return null;
        }

        // Handle token refresh from sliding window session
        handleTokenRefresh(response);

        // Handle non-OK responses
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.message || `HTTP ${response.status}: Request failed`);
            error.status = response.status;
            error.data = errorData;
            throw error;
        }

        // Return response based on type
        if (responseType === 'blob') {
            return await response.blob();
        }
        
        // Handle empty responses
        const text = await response.text();
        return text ? JSON.parse(text) : null;

    } catch (error) {
        // Don't log or throw for session expiration (already handled)
        if (error.message?.includes('session')) {
            return null;
        }
        console.error("API Fetch Error:", error);
        throw error;
    }
};

export default apiFetch;
